import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import SettingsPageLayout from './SettingsPageLayout';
import { DiarySharingSection } from './DiarySharePage';
import ServiceSharingSection from './ServiceSharingSection';
import DataSharing from '../clients/DataSharing';
import SeSharing from './SeSharing';
import SeChoicesList from './SeChoicesList';
import { can, isSelfEmployedMe } from '../permissions/access';
import '../shared/ui.css';
import './DiarySharePanel.css';

/**
 * Settings › Sharing: one page for what each team member shares, so an owner
 * can see who shares what (and why not) and sort it themselves, and a
 * self-employed stylist makes every choice that is theirs at the salon.
 *
 *   Bookings   diary sharing; for the owner also who is on the booking page
 *   Services   who is on the salon's services, and the reason anyone isn't
 *   Clients    client sharing, the same controls as Clients › Data sharing
 *   Marketing  salon-run marketing, loyalty and discount codes
 *
 * A self-employed stylist gets SeSharing's version of each tab (it replaced
 * Settings › Your choices, Oct 2026). The owner's Marketing tab and the
 * booking page list show their choices read-only (SeChoicesList).
 *
 * ?tab=bookings|services|clients|marketing picks the tab, so other screens
 * can link to one (old links with ?tab=diary land on Bookings).
 *
 * Team › Sharing shows the same tabs (SharingTabs) inside the Team page for
 * owners and managers; self-employed stylists reach it from Settings.
 * basePath keeps the tab's own address (/staff/sharing) when Team opened it
 * from /staff.
 */

const TABS = [
    { id: 'bookings', label: 'Bookings' },
    { id: 'services', label: 'Services' },
    { id: 'clients', label: 'Clients' },
    { id: 'marketing', label: 'Marketing' },
];
const OLD_TABS = { diary: 'bookings' };

export function SharingTabs(props) {
    const { location, history, basePath } = props;
    const params = new URLSearchParams((location && location.search) || '');
    const selfEmployed = isSelfEmployedMe();
    const isOwner = props.user?.userInfo?.hairdresser?.isSalonOwner === 1;
    // Self-employed stylists' choices: the owner, or a role with `team` (the server refuses anyone else).
    const seesChoices = !selfEmployed && (isOwner || can('team'));
    const tabs = TABS.filter((t) => t.id !== 'marketing' || selfEmployed || seesChoices);
    const raw = params.get('tab');
    const asked = OLD_TABS[raw] || raw;
    const tab = tabs.some((t) => t.id === asked) ? asked : 'bookings';

    const pick = (id) => {
        if (id === tab) return;
        history.replace({ pathname: basePath || location.pathname, search: `?tab=${id}` });
    };

    return (
        <div className="shp-wrap">
            <div className="shp-intro">
                <h2 className="dsp-page-title">Sharing</h2>
                <p className="dsp-page-desc">
                    {selfEmployed
                        ? 'What you share with the salon, and what it shares with you. Only you can change these.'
                        : 'What each team member shares with the salon and with each other.'}
                </p>
            </div>
            <div className="shp-tabs" role="tablist">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        className={`shp-tab ${tab === t.id ? 'shp-tab--active' : ''}`}
                        onClick={() => pick(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            {selfEmployed ? <SeSharing tab={tab} user={props.user} /> : (
                <>
                    {tab === 'bookings' && (
                        <>
                            <DiarySharingSection embedded {...props} />
                            {seesChoices && (
                                <SeChoicesList keys={['bookingPage']} title="On your booking page" lede="Employed staff are always on your booking page. Self-employed stylists choose whether they are." />
                            )}
                        </>
                    )}
                    {tab === 'services' && <ServiceSharingSection user={props.user} />}
                    {tab === 'clients' && (
                        <div className="shp-clients">
                            <DataSharing embedded history={history} />
                        </div>
                    )}
                    {tab === 'marketing' && (
                        <div className="dsp-page-content">
                            <div className="dsp-page-intro">
                                <p className="dsp-page-desc">Whether each self-employed stylist lets the salon market to their clients, joins your loyalty scheme and takes your discount codes. Only they can change these.</p>
                            </div>
                            <SeChoicesList keys={['marketing', 'loyalty', 'codes']} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function SharingPage(props) {
    return (
        <SettingsPageLayout activeTab="diary-sharing" {...props}>
            <SharingTabs {...props} />
        </SettingsPageLayout>
    );
}

function mapStateToProps(state) {
    return { ...state };
}

export default withRouter(connect(mapStateToProps)(SharingPage));
